// src/utils/keyboard.ts
// Capacitor Keyboardを利用したキーボード制御ユーティリティ

import { Keyboard, KeyboardResize } from '@capacitor/keyboard';
import { Capacitor } from '@capacitor/core';

/**
 * キーボードを表示
 */
export async function showKeyboard() {
  if (!Capacitor.isNativePlatform()) return;

  try {
    await Keyboard.show();
  } catch {
    // Keyboard操作の失敗は無視
  }
}

/**
 * キーボードを非表示
 */
export async function hideKeyboard() {
  if (!Capacitor.isNativePlatform()) return;

  try {
    await Keyboard.hide();
  } catch {
    // Keyboard操作の失敗は無視
  }
}

/**
 * キーボードのリサイズモードを設定
 * @param mode - リサイズモード（'body' | 'ionic' | 'native' | 'none'）
 */
export async function setKeyboardResize(mode: KeyboardResize) {
  if (!Capacitor.isNativePlatform()) return;

  try {
    await Keyboard.setResizeMode({ mode });
  } catch {
    // Keyboard操作の失敗は無視
  }
}

/**
 * キーボードアクセサリバーの表示/非表示を設定
 * @param isVisible - trueの場合、アクセサリバーを表示
 */
export async function setAccessoryBarVisible(isVisible: boolean) {
  if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'ios') return;

  try {
    await Keyboard.setAccessoryBarVisible({ isVisible });
  } catch {
    // Keyboard操作の失敗は無視
  }
}

/**
 * キーボードのスクロールアシストを有効化/無効化
 * @param isEnabled - trueの場合、スクロールアシストを有効化
 */
export async function setScroll(isEnabled: boolean) {
  if (!Capacitor.isNativePlatform()) return;

  try {
    await Keyboard.setScroll({ isDisabled: !isEnabled });
  } catch {
    // Keyboard操作の失敗は無視
  }
}

/**
 * GIFTERRAアプリの初期キーボード設定
 */
export async function initGifterraKeyboard() {
  if (!Capacitor.isNativePlatform()) return;

  try {
    // ボディ全体をリサイズ（キーボード表示時に画面全体がスクロール可能に）
    await setKeyboardResize(KeyboardResize.Body);

    // iOSのアクセサリバーを表示（完了ボタンなど）
    await setAccessoryBarVisible(true);

    // スクロールアシストを有効化
    await setScroll(true);
  } catch {
    // Keyboard初期化の失敗は無視
  }
}

/**
 * キーボード表示イベントリスナーを追加
 * @param callback - キーボード表示時のコールバック関数
 * @returns リスナーハンドル
 */
export function addKeyboardWillShowListener(callback: (info: { keyboardHeight: number }) => void) {
  if (!Capacitor.isNativePlatform()) return;

  return Keyboard.addListener('keyboardWillShow', callback);
}

/**
 * キーボード非表示イベントリスナーを追加
 * @param callback - キーボード非表示時のコールバック関数
 * @returns リスナーハンドル
 */
export function addKeyboardWillHideListener(callback: () => void) {
  if (!Capacitor.isNativePlatform()) return;

  return Keyboard.addListener('keyboardWillHide', callback);
}

/**
 * キーボード表示完了イベントリスナーを追加
 * @param callback - キーボード表示完了時のコールバック関数
 * @returns リスナーハンドル
 */
export function addKeyboardDidShowListener(callback: (info: { keyboardHeight: number }) => void) {
  if (!Capacitor.isNativePlatform()) return;

  return Keyboard.addListener('keyboardDidShow', callback);
}

/**
 * キーボード非表示完了イベントリスナーを追加
 * @param callback - キーボード非表示完了時のコールバック関数
 * @returns リスナーハンドル
 */
export function addKeyboardDidHideListener(callback: () => void) {
  if (!Capacitor.isNativePlatform()) return;

  return Keyboard.addListener('keyboardDidHide', callback);
}
